<template>
  <v-card outlined class="import-progress-panel mb-4">
    <v-card-text>
      <div class="d-flex align-center mb-1">
        <v-progress-circular indeterminate size="22" width="2" color="primary" class="mr-3" />
        <div>
          <div class="text-subtitle-1 font-weight-medium">
            {{ uploading ? $t('deforestation.importProgressUploading') : $t('deforestation.importProgressTitle') }}
          </div>
          <div class="text-caption grey--text text--darken-1">
            {{ uploading ? $t('deforestation.importProgressUploadingSubtitle') : $t('deforestation.importProgressSubtitle') }}
          </div>
        </div>
      </div>

      <!-- While the file is still being sent, there is no job yet: show an
           indeterminate bar instead of stale numbers from a previous import. -->
      <v-progress-linear
        v-if="uploading"
        indeterminate
        height="18"
        rounded
        color="primary"
        class="my-3"
      />
      <!-- Grey track so an empty bar reads as empty (a tinted track looked full
           at low %); percent sits beside the bar in dark text, not white-on-fill. -->
      <div v-else class="d-flex align-center my-3">
        <v-progress-linear
          :value="percent"
          height="10"
          rounded
          color="primary"
          background-color="#E0E0E0"
          :background-opacity="1"
          class="flex-grow-1"
        />
        <span class="ml-3 text-body-2 font-weight-medium primary--text" style="min-width: 36px; text-align: right;">{{ percent }}%</span>
      </div>

      <div v-if="!uploading" class="d-flex flex-wrap" style="gap: 24px;">
        <div>
          <div class="text-caption grey--text text--darken-1">
            {{ $t('deforestation.importProgressImportedList') }}
          </div>
          <div class="text-body-2 font-weight-medium">
            {{ farmsCountLabel }}
          </div>
        </div>
        <div>
          <div class="text-caption grey--text text--darken-1">
            {{ $t('deforestation.importProgressElapsed') }}
          </div>
          <div class="text-body-2 font-weight-medium">{{ elapsedLabel }}</div>
        </div>
        <div>
          <div class="text-caption grey--text text--darken-1">
            {{ $t('deforestation.importProgressEta') }}
          </div>
          <div class="text-body-2 font-weight-medium">
            {{ etaLabel }}
          </div>
        </div>
      </div>

      <v-divider class="my-3" v-if="!uploading && farms.length" />

      <div v-if="!uploading && farms.length" class="imported-list">
        <v-simple-table dense>
          <template #default>
            <!-- Sliding window: newly imported farms slide in at the top one at a
                 time and the oldest drop off the bottom (see ingestRows/dripOne). -->
            <transition-group tag="tbody" name="ip-slide">
              <tr v-for="row in farms" :key="row.id" class="imported-row">
                <td class="text-body-2">
                  <v-icon size="16" color="success" class="mr-2">mdi-check-circle</v-icon>
                  {{ farmLabel(row) }}
                </td>
                <td class="text-caption grey--text text--darken-1 text-right">
                  {{ row.farm && row.farm.country }}
                </td>
              </tr>
            </transition-group>
          </template>
        </v-simple-table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import DeforestationService from '@/_services/DeforestationService';
import { etaParts, etaLabelFromParts } from '../../components/importEta';

const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();

// Live list tuning. Farms land in batches of ~100 (one import batch), so fetch a
// batch's worth and reveal it gradually instead of jumping once per batch.
const WINDOW_SIZE = 20;   // rows visible
const FETCH_SIZE = 100;   // newest rows fetched per poll (≈ one import batch)
const MAX_QUEUE = 300;    // cap so a slow tab never builds an unbounded backlog
const MIN_DRIP_MS = 120;
const MAX_DRIP_MS = 1500;

export default {
  name: 'ImportProgressPanel',
  props: {
    diligenceId: { required: true },
    pollMs: { type: Number, default: 5000 },
    // True while the upload POST is still in flight (no job exists yet). The
    // panel must not poll or judge completion then: the report still carries the
    // PREVIOUS import's "Ready to Proceed" status, which would otherwise be read
    // as "done" and close the panel the moment Upload is clicked.
    uploading: { type: Boolean, default: false },
  },
  data() {
    return {
      reportStatus: null,
      jobStatus: null,
      completedBatches: 0,
      totalBatches: null,
      totalFarms: null,
      isEstimatedTotal: true,
      startedAt: null,
      farms: [],          // visible sliding window, newest first
      queue: [],          // newly seen rows waiting to slide in, oldest first
      primed: false,      // first list fetch shown directly (no drip)
      dripDelayMs: 500,
      dripTimer: null,
      nowTs: Date.now(),
      pollTimer: null,
      clockTimer: null,
      inFlight: false,
      done: false,
      // ETA frozen at each poll. Computing it from the per-second clock made the
      // figure drift upward between polls and then drop when a batch landed
      // (the "jumping around"), so it is only refreshed when progress is polled.
      etaPartsSnapshot: null,
    };
  },
  created() {
    // Non-reactive: ids already shown or queued, so each farm slides in once.
    this.seenIds = new Set();
  },
  computed: {
    // Progress is driven by THIS job's batch progress, never by the report's
    // total production-place count. On a re-upload the report already holds the
    // previous farms, so importedCount (report total) would exceed this job's
    // denominator (e.g. "2400 / 1000") and peg the bar at 100% while batches are
    // still running. completedBatches/totalBatches is the authoritative signal.
    percent() {
      if (this.totalBatches && this.completedBatches != null) {
        return Math.min(100, Math.round((this.completedBatches / this.totalBatches) * 100));
      }
      return 0;
    },
    // Farms processed by this job so far, scaled from batch progress and capped
    // at the job's feature total so it never exceeds the denominator.
    processedFarms() {
      if (this.totalFarms != null && this.totalBatches) {
        return Math.min(
          this.totalFarms,
          Math.round((this.completedBatches / this.totalBatches) * this.totalFarms),
        );
      }
      return this.completedBatches ? this.completedBatches * 100 : 0;
    },
    farmsCountLabel() {
      const total = this.totalFarms != null
        ? `${this.isEstimatedTotal ? '~' : ''}${this.totalFarms}`
        : '…';
      return this.$t('deforestation.importProgressFarmsCount', {
        done: this.processedFarms,
        total,
      });
    },
    elapsedMs() {
      if (!this.startedAt) return 0;
      return Math.max(0, this.nowTs - new Date(this.startedAt).getTime());
    },
    elapsedLabel() {
      return this.fmtDuration(this.elapsedMs);
    },
    etaLabel() {
      return etaLabelFromParts(this.etaPartsSnapshot, this.$t.bind(this))
        || this.$t('deforestation.importProgressEstimating');
    },
  },
  mounted() {
    this.tick();
    this.pollTimer = setInterval(this.tick, this.pollMs);
    // Keep elapsed/ETA ticking smoothly between polls.
    this.clockTimer = setInterval(() => { this.nowTs = Date.now(); }, 1000);
  },
  beforeDestroy() {
    this.stop();
  },
  watch: {
    // The POST returned and the job now exists — poll right away instead of
    // waiting up to pollMs for the next interval.
    uploading(val, old) {
      if (old && !val) this.tick();
    },
  },
  methods: {
    stop() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
      if (this.clockTimer) { clearInterval(this.clockTimer); this.clockTimer = null; }
      if (this.dripTimer) { clearTimeout(this.dripTimer); this.dripTimer = null; }
    },
    // rows arrive newest-first. The first fetch fills the window directly (on a
    // re-upload that's the farms already in the report); after that only unseen
    // rows are queued and slid in one by one by dripOne().
    ingestRows(rows) {
      const fresh = rows.filter((r) => r && !this.seenIds.has(r.id));
      fresh.forEach((r) => this.seenIds.add(r.id));
      if (!this.primed) {
        this.primed = true;
        this.farms = rows.slice(0, WINDOW_SIZE);
        return;
      }
      if (!fresh.length) return;
      // Oldest first, so the newest of the batch ends up on top last.
      this.queue.push(...fresh.slice().reverse());
      if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE);
      // Spread the backlog over roughly one batch interval so it drains about
      // when the next batch lands — a steady flow rather than a jump per batch.
      const perBatchMs = this.completedBatches > 0 && this.elapsedMs > 0
        ? this.elapsedMs / this.completedBatches
        : this.pollMs;
      this.dripDelayMs = Math.min(MAX_DRIP_MS,
        Math.max(MIN_DRIP_MS, Math.round(perBatchMs / this.queue.length)));
      this.scheduleDrip();
    },
    scheduleDrip() {
      if (this.dripTimer || !this.queue.length || this.done) return;
      this.dripTimer = setTimeout(this.dripOne, this.dripDelayMs);
    },
    dripOne() {
      this.dripTimer = null;
      const row = this.queue.shift();
      if (row) {
        this.farms.unshift(row);
        if (this.farms.length > WINDOW_SIZE) this.farms.splice(WINDOW_SIZE);
      }
      this.scheduleDrip();
    },
    fmtDuration(ms) {
      const s = Math.round(ms / 1000);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      if (h > 0) return `${h}h ${m}m`;
      if (m > 0) return `${m}m ${sec}s`;
      return `${sec}s`;
    },
    farmLabel(row) {
      const f = row.farm || {};
      return f.farmName || f.nationalFarmId || `Farm #${row.farmId}`;
    },
    async tick() {
      // No job yet while uploading — reading now would return the previous
      // import's status/progress (see `uploading` prop).
      if (this.uploading || this.inFlight || this.done) return;
      this.inFlight = true;
      try {
        const [progressRes, listRes] = await Promise.allSettled([
          DeforestationService.getImportProgress(this.diligenceId),
          DeforestationService.getImportedFarmsLite({
            diligenceReportId: Number(this.diligenceId),
            page: 1,
            limit: FETCH_SIZE, // backend orders newest first (id DESC)
          }),
        ]);

        if (progressRes.status === 'fulfilled') {
          const p = progressRes.value && progressRes.value.data && progressRes.value.data.importProgress;
          if (p) {
            this.reportStatus = p.reportStatus;
            this.jobStatus = p.jobStatus;
            this.completedBatches = p.completedBatches || 0;
            this.totalBatches = p.totalBatches;
            this.totalFarms = p.totalFarms;
            this.isEstimatedTotal = p.isEstimatedTotal;
            this.startedAt = p.startedAt;
            // Freeze the ETA on the elapsed time AT THIS POLL, so it doesn't
            // drift between polls as the clock ticks.
            const elapsedAtPoll = this.startedAt
              ? Math.max(0, Date.now() - new Date(this.startedAt).getTime())
              : 0;
            this.etaPartsSnapshot = etaParts({
              completedBatches: this.completedBatches,
              totalBatches: this.totalBatches,
              elapsedMs: elapsedAtPoll,
            });
          }
        }

        if (listRes.status === 'fulfilled') {
          const pp = listRes.value && listRes.value.data && listRes.value.data.productionPlaces;
          if (pp) {
            // Rows are a live visual of farms landing. The count and bar come
            // from the job's batch progress (see computed), not this list.
            this.ingestRows(pp.rows || []);
          }
        }

        // Report status is the authoritative done signal (handles partial
        // imports the reaper gives up on too). A Failed job still releases the
        // report, so flag it and let the parent warn instead of claiming success.
        if (norm(this.reportStatus) === 'ready to proceed') {
          this.done = true;
          this.stop();
          this.$emit('processingComplete', { failed: norm(this.jobStatus) === 'failed' });
        }
      } finally {
        this.inFlight = false;
      }
    },
  },
};
</script>

<style scoped>
.import-progress-panel {
  background: #fafafa;
}
.imported-list {
  max-height: 260px;
  overflow-y: auto;
}
.imported-row {
  background: rgba(76, 175, 80, 0.08);
}
/* Sliding window: new row drops in at the top with a brief highlight; rows
   trimmed off the bottom vanish without animating so the list doesn't jump. */
.ip-slide-enter-active {
  transition: opacity 0.35s ease, transform 0.35s ease, background-color 1.2s ease;
}
.ip-slide-enter {
  opacity: 0;
  transform: translateY(-12px);
  background-color: rgba(76, 175, 80, 0.35);
}
.ip-slide-move {
  transition: transform 0.35s ease;
}
.ip-slide-leave-active {
  display: none;
}
.imported-row td {
  border-bottom: 1px solid rgba(76, 175, 80, 0.18) !important;
}
</style>
